import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    MessageCircle,
    Send,
    X,
    Loader2,
    Check,
    CheckCheck,
} from "lucide-react";
import { toast } from "sonner";

/** Ask-instructor chat for a program.
 *
 * - Floating FAB on the student program page.
 * - Click → right drawer with messenger-style bubbles.
 * - Polls /my-thread every 8 s while open for instructor replies.
 * - Private 1:1 thread per (student, program).
 */
export default function AskInstructorChat({ programId, programTitle, currentUserId }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [thread, setThread] = useState(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [unseen, setUnseen] = useState(0);
    const scrollRef = useRef(null);
    const pollRef = useRef(null);

    const refresh = async () => {
        if (!programId) return;
        try {
            const { data } = await api.get(`/programs/${programId}/my-thread`);
            setThread(data.thread);
            setMessages(data.messages || []);
            // If the chat is NOT open, track unseen count so we can badge the FAB
            if (!open) setUnseen(data.thread?.unread_for_student || 0);
            else setUnseen(0);
        } catch (err) {
            if (err?.response?.status === 400) {
                // No instructor assigned — hide the widget gracefully
                setThread(null);
            }
        }
    };

    // Initial fetch
    useEffect(() => {
        if (!programId) return;
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programId]);

    // While open, poll every 8 s
    useEffect(() => {
        if (!open) return;
        pollRef.current = setInterval(refresh, 8000);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Auto-scroll to bottom on new message
    useEffect(() => {
        if (open && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open]);

    // Poll slowly even when closed to update unread badge
    useEffect(() => {
        if (open || !programId) return;
        const id = setInterval(refresh, 30000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, programId]);

    const send = async (e) => {
        e?.preventDefault();
        const v = text.trim();
        if (!v || sending) return;
        setSending(true);
        const optimistic = {
            id: `temp-${Date.now()}`,
            sender_role: "student",
            content: v,
            created_at: new Date().toISOString(),
            read_at: null,
        };
        setMessages((m) => [...m, optimistic]);
        setText("");
        try {
            await api.post(`/programs/${programId}/my-thread/messages`, { content: v });
            await refresh();
        } catch (err) {
            toast.error("Could not send your message. Please try again.");
            setMessages((m) => m.filter((x) => x.id !== optimistic.id));
            setText(v);
        } finally {
            setSending(false);
        }
    };

    const openDrawer = () => {
        setOpen(true);
        setUnseen(0);
        setLoading(true);
        refresh().finally(() => setLoading(false));
    };

    // Don't render if the program has no instructor
    if (!programId) return null;

    return (
        <>
            {/* Floating action button */}
            {!open && (
                <button
                    type="button"
                    onClick={openDrawer}
                    style={{ zIndex: 2147483647 }}
                    className="fixed bottom-6 right-6 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-xl shadow-primary/40 flex items-center justify-center hover:scale-105 transition"
                    title="Have doubts? Ask your instructor"
                    data-testid="ask-instructor-fab"
                >
                    <MessageCircle className="h-6 w-6" />
                    {unseen > 0 && (
                        <span
                            className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center ring-2 ring-background"
                            data-testid="ask-instructor-unread"
                        >
                            {unseen > 9 ? "9+" : unseen}
                        </span>
                    )}
                    <span className="absolute right-full mr-3 whitespace-nowrap bg-card text-foreground text-xs px-3 py-1.5 rounded shadow-md border border-border opacity-0 hover:opacity-100 pointer-events-none transition">
                        Have doubts? Ask anything.
                    </span>
                </button>
            )}

            {/* Right drawer */}
            {open && (
                <div
                    className="fixed inset-0 flex justify-end"
                    style={{ zIndex: 2147483647 }}
                    data-testid="ask-instructor-drawer"
                >
                    <div
                        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                        onClick={() => setOpen(false)}
                    />
                    <aside className="relative w-full max-w-md h-full bg-card border-l border-border flex flex-col animate-in slide-in-from-right duration-200">
                        <div className="flex items-center gap-3 px-4 py-3 border-b border-border flex-shrink-0">
                            <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                <MessageCircle className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm truncate">
                                    {thread?.instructor?.name
                                        ? `Ask ${thread.instructor.name}`
                                        : "Ask anything"}
                                </div>
                                <div className="text-[11px] text-muted-foreground truncate">
                                    {programTitle || thread?.program_title || "Your instructor"}
                                </div>
                            </div>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setOpen(false)}
                                className="h-8 w-8"
                                data-testid="ask-instructor-close"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>

                        <div
                            ref={scrollRef}
                            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gradient-to-b from-secondary/40 to-card"
                        >
                            {loading && messages.length === 0 && (
                                <div className="flex justify-center py-8">
                                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                </div>
                            )}
                            {!loading && messages.length === 0 && (
                                <div className="text-center text-sm text-muted-foreground py-10">
                                    <MessageCircle className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
                                    <p>
                                        Stuck on something? Ask your instructor a question — they&rsquo;ll reply as soon as they can.
                                    </p>
                                </div>
                            )}
                            {messages.map((m) => {
                                const mine = m.sender_role === "student";
                                return (
                                    <div
                                        key={m.id}
                                        className={`flex ${mine ? "justify-end" : "justify-start"}`}
                                        data-testid={`chat-msg-${m.sender_role}`}
                                    >
                                        <div
                                            className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed break-words ${
                                                mine
                                                    ? "bg-primary text-primary-foreground rounded-br-sm"
                                                    : "bg-accent/20 text-foreground rounded-bl-sm border border-accent/30"
                                            }`}
                                        >
                                            <div className="whitespace-pre-wrap">{m.content}</div>
                                            <div
                                                className={`text-[10px] mt-1 flex items-center gap-1 ${
                                                    mine ? "text-primary-foreground/70" : "text-muted-foreground"
                                                }`}
                                            >
                                                {new Date(m.created_at).toLocaleTimeString([], {
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                })}
                                                {mine &&
                                                    (m.read_at ? (
                                                        <CheckCheck className="h-3 w-3" />
                                                    ) : (
                                                        <Check className="h-3 w-3" />
                                                    ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <form
                            onSubmit={send}
                            className="border-t border-border px-3 py-3 flex gap-2 flex-shrink-0"
                        >
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        send();
                                    }
                                }}
                                placeholder="Ask your instructor anything… (Enter to send)"
                                className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 min-h-[40px] max-h-32"
                                rows={1}
                                data-testid="ask-instructor-input"
                            />
                            <Button
                                type="submit"
                                size="icon"
                                disabled={!text.trim() || sending}
                                className="h-10 w-10 flex-shrink-0"
                                data-testid="ask-instructor-send"
                            >
                                {sending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Send className="h-4 w-4" />
                                )}
                            </Button>
                        </form>
                    </aside>
                </div>
            )}
        </>
    );
}
